import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'icon';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'border-academy-600 bg-academy-600 text-white hover:border-academy-700 hover:bg-academy-700',
  secondary:
    'border-ink-300 bg-white text-ink-900 hover:bg-ink-100',
  ghost: 'border-transparent bg-transparent text-ink-600 hover:bg-ink-100 hover:text-ink-900',
  danger:
    'border-danger-600 bg-danger-600 text-white hover:border-danger-700 hover:bg-danger-700',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'min-h-10 px-3 text-sm',
  md: 'min-h-10 px-4 text-sm',
  icon: 'h-10 w-10 p-0',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      type = 'button',
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    const isIconOnly = size === 'icon';

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={cn(
          'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-academy-200 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-50',
          variantClasses[variant],
          sizeClasses[size],
          isIconOnly && 'gap-0',
          className,
        )}
        {...props}
      >
        {loading ? <Loader2 className={cn('h-4 w-4', !isIconOnly && 'mr-0 animate-spin')} /> : icon}
        {!isIconOnly && children}
      </button>
    );
  },
);

Button.displayName = 'Button';
